import {addHours} from 'date-fns';
import {BigCalendar, BigCalendarEvent, BigCalendarPopupProps} from 'platform/big-calendar';
import {DataStatus} from 'platform/components';

import {useState} from 'react';

import {useUpdateTestDriveMutation} from '@dms/api/sales';
import {businessCaseRoutes} from '@dms/routes';
import {handleApiError, usePermissions} from '@dms/shared';

import {composePath, useNavigate, useRequiredParams} from 'shared/web';

import {CustomerDetailsPopup} from './CustomerDetailsPopup';
import {useBigCalendarControl} from './hooks/useBigCalendarControl';
import {useCreateTestDriveAction} from './hooks/useCreateTestDriveAction';
import {TestDriveCalendarEvent} from './utils/transformTestDriveToCalendarEvent';

const DEFAULT_TEST_DRIVE_DURATION_HOURS = 1;

const formatToISOString = (date: Date): string => date.toISOString();

export function TestDrivesCalendar() {
  const {id: vehicleId} = useRequiredParams();
  const navigate = useNavigate();

  const [canEditTestDrive] = usePermissions({permissionKeys: ['editTestDrive']});
  const {canCreateTestDrive, openCreateTestDriveDialog} = useCreateTestDriveAction(vehicleId);

  const {events, isLoading, isError, currentUser} = useBigCalendarControl();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [updateTestDrive] = useUpdateTestDriveMutation();

  const handleEventClick = (event: BigCalendarEvent) => {
    const testDriveEvent = event as TestDriveCalendarEvent;

    if (testDriveEvent.businessCaseId) {
      navigate(
        composePath(businessCaseRoutes.testDrive, {
          params: {id: testDriveEvent.businessCaseId},
        })
      );
    }
  };

  const handleNavigate = (date: Date) => {
    setCurrentDate(date);
  };

  const handleDateClick = (date: Date) =>
    openCreateTestDriveDialog({
      start: date,
      end: addHours(date, DEFAULT_TEST_DRIVE_DURATION_HOURS),
    });

  const handleEventDragMove = (event: BigCalendarEvent, newStart: Date, newEnd: Date) => {
    const testDriveEvent = event as TestDriveCalendarEvent;

    if (!testDriveEvent.testDriveData) {
      return Promise.reject(new Error('Missing test drive data'));
    }

    const updatePayload = {
      userId: testDriveEvent.testDriveData.userId,
      state: testDriveEvent.testDriveData.state,
      note: testDriveEvent.testDriveData.note,
      internalNote: testDriveEvent.testDriveData.internalNote,
      scheduledAt: formatToISOString(newStart),
      returnedAt: testDriveEvent.testDriveData.hasReturnedAt ? formatToISOString(newEnd) : null,
    };

    return updateTestDrive({
      testDriveId: testDriveEvent.id,
      updateTestDriveRequestBody: updatePayload,
    })
      .unwrap()
      .then(() => undefined)
      .catch(handleApiError);
  };

  const renderEventPopup = ({event, anchorElement, isVisible}: BigCalendarPopupProps) => {
    const testDriveEvent = event as TestDriveCalendarEvent;
    return (
      <CustomerDetailsPopup
        event={testDriveEvent}
        isVisible={isVisible}
        anchorElement={anchorElement}
      />
    );
  };

  return (
    <DataStatus data={currentUser} isLoading={isLoading} isError={isError}>
      {({data}) => (
        <BigCalendar
          enableHover
          currentUser={data}
          events={events}
          allowedViews={['week', 'month']}
          defaultView="week"
          currentDate={currentDate}
          data-testid="test-drives-calendar"
          onEventClick={handleEventClick}
          onNavigate={handleNavigate}
          onEventDragMove={canEditTestDrive ? handleEventDragMove : undefined}
          onDateClick={canCreateTestDrive ? handleDateClick : undefined}
          renderEventPopup={renderEventPopup}
        />
      )}
    </DataStatus>
  );
}
